import React from 'react';
import PropTypes from 'prop-types';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import TrendOverlayTab from './TrendOverlayTab';
import ReactSelect from 'react-select';
import Draggable from 'react-draggable';
import { Resizable } from 'react-resizable';

import DashboardHeader from '../../DashboardModal/components/DashboardHeader';
import SensorPressureGraph from './SensorPressureGraph';
import { buildPressureTraces } from './pressureTraceUtils';
import { downloadCsv, formatCsvTimestamp, resolveCsvTimeZone } from '../../../utils/csvDownload';

import 'react-resizable/css/styles.css';
import './MultiDayTrendDialog.scss';

const DEFAULT_DAYS = 7;
const DAYS_OPTIONS = [2, 3, 4, 5, 6, 7, 14];

const PRESSURE_CONVERSION = {
  meter: { factor: 1, label: 'Meter' },
  bar: { factor: 0.09804139432, label: 'Bar' },
  kpa: { factor: 9.80665, label: 'kPa' },
  psi: { factor: 1.42233, label: 'PSI' },
};

const getDefaultStartDate = () => {
  const start = new Date();
  start.setDate(start.getDate() - DEFAULT_DAYS);
  return start.toISOString().split('T')[0];
};

const FONT_FAMILY = 'Helvetica, sans-serif';
const TEXT_COLOR = '#333';
const BORDER_COLOR = '#e0e0e0';
const SUBTLE_BG = '#f7f7f7';

const rootStyle = {
  fontFamily: FONT_FAMILY,
  color: TEXT_COLOR,
  fontSize: '13px',
};

const filterBarStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  padding: '10px 12px',
  marginBottom: '12px',
  background: SUBTLE_BG,
  border: `1px solid ${BORDER_COLOR}`,
  borderRadius: '4px',
  flexWrap: 'wrap',
  flexShrink: 0,
};

const labelStyle = {
  fontSize: '12px',
  fontWeight: 600,
  color: TEXT_COLOR,
  marginRight: '6px',
  fontFamily: FONT_FAMILY,
};

const inputStyle = {
  fontSize: '13px',
  padding: '4px 8px',
  border: `1px solid ${BORDER_COLOR}`,
  borderRadius: '4px',
  fontFamily: FONT_FAMILY,
  color: TEXT_COLOR,
  background: '#fff',
  height: '30px',
  boxSizing: 'border-box',
};

const selectStyle = {
  ...inputStyle,
  minWidth: '60px',
};

const sensorSelectStyles = {
  container: (base) => ({ ...base, minWidth: '280px', flex: '1 1 auto' }),
  control: (base) => ({ ...base, minHeight: '30px', borderColor: BORDER_COLOR, fontFamily: FONT_FAMILY, fontSize: '13px' }),
  valueContainer: (base) => ({ ...base, padding: '0 6px' }),
  multiValue: (base) => ({ ...base, background: '#e8f0f5' }),
  multiValueLabel: (base) => ({ ...base, color: TEXT_COLOR, fontSize: '12px' }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const tableHeaderCellStyle = {
  padding: '8px 10px',
  textAlign: 'left',
  fontWeight: 600,
  fontSize: '12px',
  textTransform: 'uppercase',
  letterSpacing: '0.4px',
  color: '#666',
  borderBottom: `1px solid ${BORDER_COLOR}`,
  background: SUBTLE_BG,
  verticalAlign: 'middle',
  position: 'sticky',
  top: 0,
  zIndex: 1,
  fontFamily: FONT_FAMILY,
};

const tableCellStyle = {
  padding: '10px',
  fontSize: '13px',
  borderBottom: `1px solid ${BORDER_COLOR}`,
  verticalAlign: 'middle',
  fontFamily: FONT_FAMILY,
  color: TEXT_COLOR,
};

const statKeyStyle = {
  display: 'inline-block',
  width: '32px',
  color: '#888',
  fontSize: '12px',
};

const statValueStyle = {
  fontWeight: 600,
  fontSize: '13px',
};

const getSensorLabel = (sensor) => {
  if (!sensor) return '';
  return sensor.aqs_logger_id ? sensor.aqs_logger_id : `Sensor ${sensor.ID}`;
};

const computeStats = (readingData, factor) => {
  const values = (readingData || [])
    .map((r) => parseFloat(r.measurement_m))
    .filter((v) => !isNaN(v));
  if (!values.length) return null;
  const min = Math.min(...values) * factor;
  const max = Math.max(...values) * factor;
  const avg = (values.reduce((a, b) => a + b, 0) / values.length) * factor;
  return {
    min: min.toFixed(2),
    max: max.toFixed(2),
    avg: avg.toFixed(2),
  };
};

export default class MultiDayTrendDialog extends React.Component {
  static get propTypes() {
    return {
      open: PropTypes.bool.isRequired,
      onClose: PropTypes.func.isRequired,
      projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      sensorId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      lastPressureSample: PropTypes.object,
      transientThresholds: PropTypes.object,
      timeZone: PropTypes.string,
      projectTimezone: PropTypes.string,
      fetchMultiDayReading: PropTypes.func.isRequired,
      fetchMultiDayTransients: PropTypes.func.isRequired,
      fetchPressureAlarmsForSensor: PropTypes.func,
      fetchTransientReading: PropTypes.func,
      pressureUnit: PropTypes.string,
      pressureSensors: PropTypes.array,
      initialSensorIds: PropTypes.array,
      initialTab: PropTypes.oneOf(['per_sensor', 'overlay']),
    };
  }

  getInitialSensorIds = () => {
    const { initialSensorIds, sensorId } = this.props;
    const primaryId = sensorId != null ? String(sensorId) : null;
    const ids = initialSensorIds && initialSensorIds.length > 0
      ? initialSensorIds.map(String)
      : [];

    if (!primaryId) return ids;
    return [primaryId, ...ids.filter((id) => id !== primaryId)];
  };

  constructor(props) {
    super(props);
    this.state = {
      startDate: getDefaultStartDate(),
      days: DEFAULT_DAYS,
      sensorData: {},
      selectedSensorIds: this.getInitialSensorIds(),
      isFetching: false,
      error: null,
      ...this.getInitialLayout(),
      isMaximized: false,
      activeTab: props.initialTab || 'per_sensor',
      overlayMounted: props.initialTab === 'overlay',
    };
  }

  getInitialLayout = () => {
    const width = Math.min(1280, Math.round(window.innerWidth * 0.95));
    const height = Math.min(720, Math.round(window.innerHeight * 0.85));
    return {
      size: { width, height },
      position: {
        x: Math.max(0, Math.round((window.innerWidth - width) / 2)),
        y: 40,
      },
    };
  };

  componentDidMount() {
    window.addEventListener('keydown', this.handleKeyDown);
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.handleKeyDown);
  }

  handleKeyDown = (e) => {
    if (e.key === 'Escape' && this.props.open) {
      this.props.onClose();
    }
  };

  componentDidUpdate(prevProps) {
    if (this.props.open && !prevProps.open) {
      this.setState({
        startDate: getDefaultStartDate(),
        days: DEFAULT_DAYS,
        sensorData: {},
        selectedSensorIds: this.getInitialSensorIds(),
        error: null,
        ...this.getInitialLayout(),
        isMaximized: false,
        activeTab: this.props.initialTab || 'per_sensor',
        overlayMounted: this.props.initialTab === 'overlay',
      }, () => this.handleLoad());
    } else if (
      this.props.open &&
      prevProps.open &&
      this.props.initialSensorIds !== prevProps.initialSensorIds &&
      this.props.initialSensorIds &&
      this.props.initialSensorIds.length > 0
    ) {
      // A new selection was pushed while the dialog is already open
      // (e.g. a second polygon selection on the map).
      this.setState({
        sensorData: {},
        selectedSensorIds: this.getInitialSensorIds(),
        error: null,
      }, () => this.handleLoad());
    }
  }

  handleDrag = (_, data) => {
    if (!this.state.isMaximized) {
      this.setState({ position: { x: data.x, y: data.y } });
    }
  };

  handleResize = (_, { size: newSize }) => {
    if (!this.state.isMaximized) {
      this.setState({ size: { width: newSize.width, height: newSize.height } });
    }
  };

  toggleMaximize = () => {
    this.setState((s) => ({ isMaximized: !s.isMaximized }));
  };

  getEndDate = () => {
    const { startDate, days } = this.state;
    const end = new Date(startDate);
    end.setDate(end.getDate() + days - 1);
    return end.toISOString().split('T')[0];
  };

  handleLoad = () => {
    const { startDate, selectedSensorIds } = this.state;
    if (!startDate) {
      this.setState({ error: 'Please select a start date' });
      return;
    }

    const { projectId, fetchMultiDayReading, fetchMultiDayTransients, fetchPressureAlarmsForSensor } = this.props;
    const endDate = this.getEndDate();

    this.setState({ isFetching: true, error: null });

    const fetchSensor = (id) => Promise.all([
      fetchMultiDayReading(projectId, id, startDate, endDate).catch(() => []),
      fetchMultiDayTransients(projectId, id, startDate, endDate).catch(() => []),
      fetchPressureAlarmsForSensor ? fetchPressureAlarmsForSensor(projectId, id).catch(() => []) : Promise.resolve([]),
    ]).then(([reading, transients, alarms]) => [id, { reading: reading || [], transients: transients || [], alarms: alarms || [] }]);

    Promise.all(selectedSensorIds.map(fetchSensor))
      .then((entries) => {
        const sensorData = entries.reduce((acc, [id, data]) => {
          acc[id] = data;
          return acc;
        }, {});
        this.setState({ sensorData, isFetching: false });
      })
      .catch(() => {
        this.setState({ sensorData: {}, isFetching: false, error: 'Failed to fetch data' });
      });
  };

  buildTracesForSensor = (sensorId, isPrimary) => {
    const { lastPressureSample, transientThresholds, timeZone, projectTimezone } = this.props;
    const { sensorData, startDate } = this.state;
    const data = sensorData[sensorId];
    if (!data?.reading?.length) return [];

    const fromTime = new Date(startDate).getTime();
    const end = new Date(this.getEndDate());
    end.setHours(23, 59, 59, 999);
    const toTime = end.getTime();

    return buildPressureTraces({
      readingData: data.reading,
      timeZone,
      projectTimezone,
      pressureAlarms: data.alarms,
      transients: data.transients,
      transientThresholds,
      // Only show configured min/max for the primary sensor (we don't have it for others).
      lastPressureSample: isPrimary ? lastPressureSample : null,
      fromTime,
      toTime,
      t: this.context.t,
    });
  };

  handleDownloadCsv = () => {
    const { pressureUnit, timeZone, projectTimezone, pressureSensors } = this.props;
    const { sensorData, startDate, selectedSensorIds } = this.state;
    const conversion = PRESSURE_CONVERSION[pressureUnit] || PRESSURE_CONVERSION.meter;
    const unitLabel = conversion.label.toLowerCase();
    const { ianaTimeZone, label: tzLabel } = resolveCsvTimeZone(timeZone, projectTimezone);
    const endDate = this.getEndDate();
    const fileBase = `pressure_multiday_${startDate}_to_${endDate}`;

    const labelFor = (id) => {
      const sensor = (pressureSensors || []).find((s) => String(s.ID) === id);
      return sensor ? getSensorLabel(sensor) : '';
    };
    const convert = (v) => (v != null ? (parseFloat(v) * conversion.factor).toFixed(4) : '');

    const trendRows = [];
    const transientRows = [];
    const alarmRows = [];
    selectedSensorIds.forEach((id) => {
      const data = sensorData[id];
      const logger = labelFor(id);
      (data?.reading || []).forEach((r) => {
        trendRows.push([id, logger, formatCsvTimestamp(r.time_utc, ianaTimeZone), convert(r.measurement_m)]);
      });
      (data?.transients || []).forEach((tr) => {
        transientRows.push([id, logger, formatCsvTimestamp(tr.event_time, ianaTimeZone), convert(tr.min_reading), convert(tr.max_reading)]);
      });
      (data?.alarms || []).forEach((a) => {
        alarmRows.push([id, logger, formatCsvTimestamp(a.alarm_time, ianaTimeZone), convert(a.value_m)]);
      });
    });

    if (trendRows.length) {
      downloadCsv(`${fileBase}_trend.csv`, ['sensor_id', 'logger_id', `timestamp (${tzLabel})`, `pressure_${unitLabel}`], trendRows);
    }
    if (transientRows.length) {
      downloadCsv(`${fileBase}_transients.csv`, ['sensor_id', 'logger_id', `event_time (${tzLabel})`, `min_${unitLabel}`, `max_${unitLabel}`], transientRows);
    }
    if (alarmRows.length) {
      downloadCsv(`${fileBase}_alarms.csv`, ['sensor_id', 'logger_id', `alarm_time (${tzLabel})`, `value_${unitLabel}`], alarmRows);
    }
  };

  // SensorPressureGraph derives chart size as 0.38*(pageWidth-10) x 0.3*pageHeight;
  // invert the formula to feed pixels we want for the per-row chart.
  getRowUiInfo = () => {
    const { size, isMaximized } = this.state;
    const modalWidth = isMaximized ? window.innerWidth : (size?.width || 1240);
    // Chart cell width ≈ modalWidth - first three columns (64+160+120) - padding/borders.
    const targetWidth = Math.max(400, modalWidth - 420);
    const targetHeight = 240;
    return {
      pageWidth: Math.round((targetWidth / 0.38) + 10),
      pageHeight: Math.round(targetHeight / 0.3),
    };
  };

  getSensorOptions = () => {
    const { pressureSensors } = this.props;
    return (pressureSensors || [])
      .map((sensor) => ({
        value: String(sensor.ID),
        label: `${sensor.ID} — ${getSensorLabel(sensor)}`,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  };

  handleSensorSelectionChange = (selected) => {
    const { sensorId } = this.props;
    const primaryId = sensorId != null ? String(sensorId) : null;
    const selectedIds = (selected || []).map((opt) => opt.value);
    const ids = primaryId
      ? [primaryId, ...selectedIds.filter((id) => id !== primaryId)]
      : selectedIds;
    this.setState({ selectedSensorIds: ids });
  };

  renderSensorRow = (sensorId) => {
    const { sensorData } = this.state;
    const { sensorId: primarySensorId, pressureUnit, pressureSensors } = this.props;
    const primaryId = primarySensorId != null ? String(primarySensorId) : null;
    const isPrimary = sensorId === primaryId;
    const sensor = (pressureSensors || []).find((s) => String(s.ID) === sensorId);
    const conversion = PRESSURE_CONVERSION[pressureUnit] || PRESSURE_CONVERSION.meter;
    const data = sensorData[sensorId];
    const stats = computeStats(data?.reading, conversion.factor);
    const traces = this.buildTracesForSensor(sensorId, isPrimary);
    const rowUiInfo = this.getRowUiInfo();

    return (
      <tr key={sensorId}>
        <td style={{ ...tableCellStyle, width: '64px', fontWeight: 600 }}>
          {sensorId}
        </td>
        <td style={{ ...tableCellStyle, width: '160px', color: '#555' }}>
          {sensor ? getSensorLabel(sensor) : '-'}
        </td>
        <td style={{ ...tableCellStyle, width: '120px', whiteSpace: 'nowrap', lineHeight: '1.6' }}>
          {stats ? (
            <>
              <div><span style={statKeyStyle}>avg</span><span style={statValueStyle}>{stats.avg}</span></div>
              <div><span style={statKeyStyle}>min</span><span style={statValueStyle}>{stats.min}</span></div>
              <div><span style={statKeyStyle}>max</span><span style={statValueStyle}>{stats.max}</span></div>
            </>
          ) : (
            <span style={{ color: '#999' }}>{this.context.t('no_data')}</span>
          )}
        </td>
        <td style={tableCellStyle}>
          {traces.length > 0 ? (
            <SensorPressureGraph
              plotData={traces}
              uiInfo={rowUiInfo}
              isFetching={false}
              rangemode="normal"
              allowChangeUnits
              compactLayout
            />
          ) : (
            <div style={{ color: '#999', textAlign: 'center', padding: '20px', fontFamily: FONT_FAMILY }}>
              {this.context.t('no_data')}
            </div>
          )}
        </td>
      </tr>
    );
  };

  render() {
    const { open, onClose, sensorId, pressureUnit, projectId, timeZone, projectTimezone,
            transientThresholds, pressureSensors, fetchMultiDayReading, fetchMultiDayTransients,
            fetchTransientReading } = this.props;
    if (!open) return null;

    const {
      startDate, days, isFetching, error, selectedSensorIds, sensorData,
      position, size, isMaximized, activeTab, overlayMounted,
    } = this.state;

    const sensorOptions = this.getSensorOptions();
    const primaryId = sensorId != null ? String(sensorId) : null;
    // Preserve selection order so the pinned primary sensor stays visually
    // first and newly selected sensors are appended after it.
    const selectedOptions = selectedSensorIds
      .map((id) => sensorOptions.find((opt) => opt.value === id))
      .filter(Boolean);
    const hasAnyData = Object.values(sensorData).some((d) => d?.reading?.length || d?.transients?.length || d?.alarms?.length);
    const conversion = PRESSURE_CONVERSION[pressureUnit] || PRESSURE_CONVERSION.meter;

    const content = (
      <div style={{ ...rootStyle, width: '100%', display: 'flex', flexDirection: 'column', flex: '1 1 auto', minHeight: 0 }}>
        <Tabs
          value={activeTab}
          onChange={(e, v) => this.setState(
            (s) => ({ activeTab: v, overlayMounted: s.overlayMounted || v === 'overlay' }),
            // The hidden tab's Plotly charts are size 0; poke them to re-measure.
            () => window.dispatchEvent(new Event('resize'))
          )}
          sx={{ minHeight: '36px', marginBottom: '8px', flexShrink: 0 }}
        >
          <Tab value="per_sensor" label={this.context.t('per_sensor')} sx={{ minHeight: '36px', padding: '6px 12px' }} />
          <Tab value="overlay" label={this.context.t('overlay')} sx={{ minHeight: '36px', padding: '6px 12px' }} />
        </Tabs>

        <div style={filterBarStyle}>
          {activeTab === 'per_sensor' && (
            <>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span style={labelStyle}>{this.context.t('start_date')}:</span>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => this.setState({ startDate: e.target.value })}
                  style={inputStyle}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span style={labelStyle}>{this.context.t('days')}:</span>
                <select
                  value={days}
                  onChange={(e) => this.setState({ days: parseInt(e.target.value, 10) })}
                  style={selectStyle}
                >
                  {DAYS_OPTIONS.map((d) => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </div>
            </>
          )}
          <div style={{ display: 'flex', alignItems: 'center', flex: '1 1 320px' }}>
            <span style={labelStyle}>{this.context.t('sensors')}:</span>
            <ReactSelect
              isMulti
              closeMenuOnSelect={false}
              options={sensorOptions}
              value={selectedOptions}
              onChange={this.handleSensorSelectionChange}
              placeholder={this.context.t('select')}
              menuPortalTarget={document.body}
              styles={sensorSelectStyles}
              isOptionDisabled={(opt) => opt.value === primaryId}
            />
          </div>
          {activeTab === 'per_sensor' && (
            <>
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={this.handleLoad}
                disabled={!startDate || isFetching}
              >
                {this.context.t('load')}
              </Button>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                onClick={this.handleDownloadCsv}
                disabled={isFetching || !hasAnyData}
              >
                {`${this.context.t('download')} CSV`}
              </Button>
            </>
          )}
        </div>

        {error && activeTab === 'per_sensor' && (
          <div style={{ color: 'red', marginBottom: '8px', fontSize: '0.875rem' }}>{error}</div>
        )}

        {/* Both tab bodies stay mounted (hidden via display) so switching
            tabs never resets the overlay's zoom/timescale/fetched state. */}
        {(overlayMounted || activeTab === 'overlay') && (
          <div style={{ display: activeTab === 'overlay' ? 'flex' : 'none', flexDirection: 'column', flex: '1 1 auto', minHeight: 0 }}>
            <TrendOverlayTab
              projectId={projectId}
              sensorIds={selectedSensorIds}
              pressureSensors={pressureSensors}
              transientThresholds={transientThresholds}
              timeZone={timeZone}
              projectTimezone={projectTimezone}
              pressureUnit={pressureUnit}
              fetchMultiDayReading={fetchMultiDayReading}
              fetchMultiDayTransients={fetchMultiDayTransients}
              fetchTransientReading={fetchTransientReading}
              t={this.context.t}
            />
          </div>
        )}

        <div style={{ display: activeTab === 'per_sensor' ? 'flex' : 'none', flexDirection: 'column', flex: '1 1 auto', minHeight: 0 }}>
          {hasAnyData ? (
            <div style={{ flex: '1 1 auto', overflowY: 'auto', overflowX: 'hidden', border: `1px solid ${BORDER_COLOR}`, borderRadius: '4px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: FONT_FAMILY }}>
                <thead>
                  <tr>
                    <th style={{ ...tableHeaderCellStyle, width: '64px' }}>{this.context.t('id')}</th>
                    <th style={{ ...tableHeaderCellStyle, width: '160px' }}>{this.context.t('logger_id')}</th>
                    <th style={{ ...tableHeaderCellStyle, width: '120px' }}>{`stats (${conversion.label})`}</th>
                    <th style={tableHeaderCellStyle}>{this.context.t('graph')}</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedSensorIds.map((id) => this.renderSensorRow(id))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 0', fontFamily: FONT_FAMILY }}>
              <p style={{ color: '#666', margin: 0 }}>
                {isFetching ? this.context.t('loading') : this.context.t('no_data')}
              </p>
            </div>
          )}
        </div>
      </div>
    );

    const title = this.context.t('multi_day_trend');

    const headerProps = {
      onClose,
      onMaximize: this.toggleMaximize,
      isMaximized,
      title,
      showMinimize: false,
      t: this.context.t,
    };

    // One element tree for both windowed and maximized states: switching
    // trees on maximize would remount the content and wipe the overlay
    // tab's local state (zoom, timescale, fetched data).
    return (
      <div className={`multi-day-trend-modal-overlay${isMaximized ? ' maximized' : ''}`}>
        <Draggable
          handle=".dashboard-header"
          position={isMaximized ? { x: 0, y: 0 } : position}
          onDrag={this.handleDrag}
          bounds="parent"
          disabled={isMaximized}
        >
          <Resizable
            width={size.width}
            height={size.height}
            onResize={this.handleResize}
            minConstraints={[640, 360]}
            maxConstraints={[Math.round(window.innerWidth * 0.98), Math.round(window.innerHeight * 0.98)]}
            resizeHandles={isMaximized ? [] : ['se', 'e', 's']}
          >
            <Paper
              className={`multi-day-trend-modal${isMaximized ? ' maximized' : ''}`}
              style={isMaximized ? undefined : { width: size.width, height: size.height }}
            >
              <DashboardHeader {...headerProps} />
              <div className="multi-day-trend-modal-content">{content}</div>
            </Paper>
          </Resizable>
        </Draggable>
      </div>
    );
  }
}

MultiDayTrendDialog.contextTypes = {
  t: PropTypes.func.isRequired,
};
